import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "@phosphor-icons/react";
import { Reveal, FadeIn, RiseHeading } from "@/components/motion";
import { WhatsAppButton, CallButton } from "@/components/CtaButtons";
import Marquee from "@/components/Marquee";
import FinalCTA from "@/components/FinalCTA";
import ShortsGrid from "@/components/ShortsGrid";
import {
  IMG,
  BRAND,
  HOME_SERVICES,
  PROJECTS,
  WHY_US,
  PROCESS,
  BRANDS,
} from "@/lib/data";

const STATS = ["15+ Years Experience", "Residential & Commercial", "Custom Solutions", "Ahmedabad & Nearby"];

/* ---------------- Hero ---------------- */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <section ref={ref} data-testid="hero" className="relative overflow-hidden bg-offwhite">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 pb-20 pt-14 md:grid-cols-12 md:gap-8 md:px-8 md:pb-28 md:pt-20">
        <div className="flex flex-col justify-center md:col-span-6">
          <FadeIn delay={0.15}>
            <span className="eyebrow rule">Ahmedabad · Since 15+ Years</span>
          </FadeIn>

          <RiseHeading className="mt-6 font-display text-5xl font-medium leading-[1.02] tracking-tight text-ink md:text-[4.4rem] md:leading-[1]" delay={0.05}>
            Built with Clarity. <br />
            <span className="italic">Designed to Last.</span>
          </RiseHeading>

          <Reveal delay={0.25}>
            <p className="mt-7 max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg">
              For over 15 years, Nagar Glass has delivered custom glass and aluminium solutions for
              homes, offices, retail spaces and commercial projects across Ahmedabad.
            </p>
          </Reveal>

          <Reveal delay={0.35}>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <WhatsAppButton testid="hero-whatsapp-btn" />
              <CallButton label="Call Now" testid="hero-call-btn" />
            </div>
          </Reveal>

          <Reveal delay={0.5}>
            <div className="mt-10 grid grid-cols-2 gap-y-3 text-xs md:mt-12 md:grid-cols-4 md:gap-x-4">
              {STATS.map((s) => (
                <div key={s} className="flex items-center gap-2 text-muted-foreground">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                  <span className="font-medium">{s}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-6">
          <div className="relative">
            <FadeIn delay={0.15}>
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-sm md:aspect-auto md:h-[640px]">
                <motion.img
                  src={IMG.heroFacade}
                  alt="Modern architectural glass facade in Ahmedabad"
                  style={{ y }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
            </FadeIn>
            <Reveal delay={0.4}>
              <div className="absolute -bottom-8 -left-4 hidden max-w-[280px] rounded-sm border border-black/10 bg-offwhite p-5 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.25)] md:block">
                <div className="flex items-center gap-3">
                  <img src={BRAND.ownerPhoto} alt="Founder" className="h-14 w-14 rounded-full object-cover" />
                  <div>
                    <div className="font-display text-base font-semibold text-ink">{BRAND.ownerName}</div>
                    <div className="text-[0.65rem] uppercase tracking-[0.15em] text-muted-foreground">
                      {BRAND.ownerTitle}
                    </div>
                  </div>
                </div>
                <p className="mt-4 text-sm italic leading-relaxed text-muted-foreground">
                  "Every piece of glass and aluminium we install is measured, made and finished by
                  our own team."
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- About Preview ---------------- */
const AboutPreview = () => (
  <section data-testid="about-preview" className="border-y border-black/10 bg-white">
    <div className="mx-auto grid max-w-[1400px] gap-12 px-5 py-24 md:grid-cols-12 md:gap-16 md:px-8 md:py-32">
      <div className="md:col-span-5">
        <Reveal>
          <div className="relative overflow-hidden rounded-sm border border-black/10">
            <img src={IMG.craftsman} alt="Craftsman installing glass" className="aspect-[4/5] h-full w-full object-cover" />
          </div>
        </Reveal>
      </div>
      <div className="flex flex-col justify-center md:col-span-7">
        <span className="eyebrow rule">About Nagar Glass</span>
        <RiseHeading className="mt-5 font-display text-3xl font-medium leading-[1.1] tracking-tight text-ink md:text-5xl">
          Craftsmanship You Can See. <br />
          <span className="italic text-charcoal">Quality You Can Trust.</span>
        </RiseHeading>
        <Reveal delay={0.15}>
          <p className="mt-7 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Nagar Glass is a glass and aluminium company based in Ahmedabad, working with
            homeowners, architects, interior designers, builders, offices and retail spaces. For
            more than 15 years we have measured, fabricated and installed glass and aluminium work
            that has to look right and keep working every day.
          </p>
        </Reveal>
        <Reveal delay={0.22}>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
            From a single shower enclosure to complete commercial glazing, the approach stays the
            same — accurate measurement, quality material and clean installation.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <Link
            to="/about"
            data-testid="about-learn-more"
            className="group mt-8 inline-flex w-fit items-center gap-2 border-b border-ink pb-1 text-xs font-semibold uppercase tracking-[0.18em] text-ink"
          >
            Learn More <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Services ---------------- */
const Services = () => (
  <section data-testid="home-services" className="bg-offwhite py-24 md:py-32">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8">
      <div className="grid grid-cols-1 items-end gap-8 md:grid-cols-12">
        <div className="md:col-span-8">
          <span className="eyebrow rule">What We Do</span>
          <RiseHeading className="mt-5 font-display text-3xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl">
            Glass and aluminium, <span className="italic">made to measure.</span>
          </RiseHeading>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Complete residential and commercial solutions, fabricated and installed by our own
              team.
            </p>
          </Reveal>
        </div>
        <div className="md:col-span-4 md:text-right">
          <Link to="/services" data-testid="view-all-services" className="group inline-flex w-fit items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-ink hover-underline">
            View All Services <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>

      <div className="mt-14 grid grid-cols-2 gap-5 md:grid-cols-4 md:gap-6">
        {HOME_SERVICES.map((s, i) => (
          <Reveal key={s.title} delay={(i % 4) * 0.06}>
            <Link to="/services" data-testid={`home-service-${i}`} className="group block h-full">
              <div className="overflow-hidden rounded-sm border border-black/10 bg-white">
                <div className="aspect-[5/4] overflow-hidden">
                  <img src={s.img} alt={s.title} className="h-full w-full object-cover transition-transform duration-[1.1s] ease-out group-hover:scale-110" />
                </div>
                <div className="flex items-center justify-between p-5">
                  <h3 className="font-display text-base font-medium leading-tight text-ink md:text-lg">{s.title}</h3>
                  <ArrowUpRight size={16} className="shrink-0 text-muted-foreground transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" />
                </div>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Residential & Commercial ---------------- */
const SplitCard = ({ img, tag, title, desc, testid }) => (
  <Link to="/services" data-testid={testid} className="group relative block aspect-[4/5] overflow-hidden rounded-sm md:aspect-[3/4]">
    <img src={img} alt={title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.3s] ease-out group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/5" />
    <div className="absolute inset-0 flex flex-col justify-end p-7 md:p-10">
      <span className="text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-gold">{tag}</span>
      <h3 className="mt-3 font-display text-2xl font-medium tracking-tight text-white md:text-4xl">{title}</h3>
      <p className="mt-4 max-w-md text-sm leading-relaxed text-white/75">{desc}</p>
      <span className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-white">
        Explore <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </div>
  </Link>
);

const ResidentialCommercial = () => (
  <section data-testid="residential-commercial" className="border-t border-black/10 bg-white py-24 md:py-32">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8">
      <div className="max-w-3xl">
        <span className="eyebrow rule">Where We Work</span>
        <RiseHeading className="mt-5 font-display text-3xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl">
          Two worlds. <span className="italic">One standard of finish.</span>
        </RiseHeading>
      </div>
      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
        <Reveal>
          <SplitCard testid="residential-card" img={IMG.living} tag="Residential" title="Residential Solutions" desc="Glass and aluminium solutions for modern homes, including windows, doors, showers, mirrors, partitions, railings and custom glass work." />
        </Reveal>
        <Reveal delay={0.12}>
          <SplitCard testid="commercial-card" img={IMG.office} tag="Commercial" title="Commercial Solutions" desc="Solutions for offices, stores, showrooms and commercial properties, including storefronts, partitions, glass cabins, aluminium systems and architectural glass." />
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Founder Section ---------------- */
const FounderSection = () => (
  <section data-testid="founder-section" className="bg-cream/50 py-24 md:py-32">
    <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:grid-cols-12 md:gap-16 md:px-8">
      <div className="md:col-span-5">
        <Reveal>
          <div className="relative">
            <div className="absolute -left-4 -top-4 hidden h-full w-full rounded-sm border border-gold md:block" />
            <div className="relative overflow-hidden rounded-sm border border-black/10">
              <img src={BRAND.ownerPhoto} alt={BRAND.ownerName} className="aspect-[4/5] h-full w-full object-cover" />
            </div>
          </div>
        </Reveal>
      </div>
      <div className="flex flex-col justify-center md:col-span-7">
        <span className="eyebrow rule">Meet the Founder</span>
        <RiseHeading className="mt-5 font-display text-3xl font-medium leading-[1.1] tracking-tight text-ink md:text-5xl">
          Fifteen years, <span className="italic">one obsession — a perfect finish.</span>
        </RiseHeading>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Nagar Glass was founded on the belief that glass and aluminium work must be treated with
            the same precision as fine architecture. Every measurement, cut and installation is
            supervised personally to make sure the final result speaks for itself.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
            From a single home enclosure to complete commercial glazing, our commitment is the same
            — honest work, quality material and a finish you can trust for years.
          </p>
        </Reveal>
        <Reveal delay={0.35}>
          <div className="mt-8 border-l-2 border-gold pl-5">
            <div className="font-display text-xl font-semibold text-ink">{BRAND.ownerName}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{BRAND.ownerTitle}</div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Projects Preview ---------------- */
const ProjectsPreview = () => {
  const featured = PROJECTS.slice(0, 6);
  return (
    <section data-testid="home-projects" className="bg-offwhite py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-5 md:px-8">
        <div className="grid grid-cols-1 items-end gap-8 md:grid-cols-12">
          <div className="md:col-span-8">
            <span className="eyebrow rule">Selected Work</span>
            <RiseHeading className="mt-5 font-display text-3xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl">
              Projects across <span className="italic">Ahmedabad.</span>
            </RiseHeading>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
                Residential, commercial, showers, mirrors, storefronts and aluminium systems.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/projects" data-testid="view-all-projects" className="group inline-flex w-fit items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-ink hover-underline">
              View All Projects <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08}>
              <Link to="/projects" data-testid={`home-project-${i}`} className="group block h-full">
                <div className="overflow-hidden rounded-sm border border-black/10 bg-white">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.img} alt={p.title} className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
                  </div>
                  <div className="flex items-start justify-between gap-3 p-5">
                    <div>
                      <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-gold">{p.category}</span>
                      <h3 className="mt-2 font-display text-lg font-medium text-ink">{p.title}</h3>
                    </div>
                    <ArrowUpRight size={18} className="mt-1 shrink-0 text-muted-foreground transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Why Us ---------------- */
const WhyUs = () => (
  <section data-testid="why-us" className="bg-ink py-24 text-offwhite md:py-32">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8">
      <div className="max-w-3xl">
        <span className="eyebrow text-white/50 rule">Why Nagar Glass</span>
        <RiseHeading className="mt-5 font-display text-3xl font-medium leading-[1.05] tracking-tight md:text-5xl">
          Reasons clients <span className="italic text-gold">keep calling us back.</span>
        </RiseHeading>
      </div>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 md:gap-x-14">
        {WHY_US.map((w, i) => (
          <Reveal key={w.n} delay={(i % 2) * 0.08}>
            <div className="flex gap-6 border-t border-white/15 py-8 md:gap-8 md:py-10">
              <span className="font-display text-2xl italic text-gold md:text-3xl">{w.n}</span>
              <div>
                <h3 className="font-display text-xl font-medium tracking-tight md:text-2xl">{w.title}</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-white/60 md:text-base">{w.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Process ---------------- */
const Process = () => (
  <section data-testid="process" className="bg-white py-24 md:py-32">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8">
      <span className="eyebrow rule">Our Process</span>
      <RiseHeading className="mt-5 font-display text-3xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl">
        Four steps, <span className="italic">start to finish.</span>
      </RiseHeading>
      <div className="mt-14 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
        {PROCESS.map((p, i) => (
          <Reveal key={p.n} delay={i * 0.1}>
            <div className="border-t-2 border-ink pt-6">
              <span className="font-display text-4xl italic text-gold md:text-5xl">{p.n}</span>
              <h3 className="mt-4 font-display text-xl font-medium tracking-tight text-ink">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Brands ---------------- */
const BrandsSection = () => (
  <section data-testid="brands" className="border-y border-black/10 bg-cream/40 py-20 md:py-24">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8">
      <div className="max-w-3xl">
        <span className="eyebrow rule">Materials & Brands</span>
        <RiseHeading className="mt-5 font-display text-3xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl">
          We build with brands <span className="italic">we can stand behind.</span>
        </RiseHeading>
        <Reveal delay={0.15}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Glass, mirrors, aluminium systems and hardware are sourced from established
            manufacturers — so the finish holds up years after installation.
          </p>
        </Reveal>
      </div>
      <div className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-black/10 bg-black/10 md:grid-cols-3 lg:grid-cols-6">
        {BRANDS.map((b, i) => (
          <Reveal key={b.name} delay={(i % 6) * 0.05}>
            <div className="flex h-full flex-col justify-center bg-offwhite p-6 md:p-8">
              <div className="font-display text-xl font-semibold tracking-tight text-ink md:text-2xl">{b.name}</div>
              <div className="mt-2 text-xs leading-relaxed text-muted-foreground">{b.desc}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Reels on Home ---------------- */
const VideosSection = () => (
  <section data-testid="home-videos" className="bg-white py-24 md:py-32">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8">
      <div className="grid grid-cols-1 items-end gap-6 md:grid-cols-12">
        <div className="md:col-span-8">
          <span className="eyebrow rule">Videos & Reels</span>
          <RiseHeading className="mt-5 font-display text-3xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl">
            Work <span className="italic">in motion.</span>
          </RiseHeading>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              A closer look at installations, fabrication detail and finished glass and aluminium
              work — straight from our site and workshop.
            </p>
          </Reveal>
        </div>
        <div className="md:col-span-4 md:text-right">
          <Link to="/projects" className="group inline-flex w-fit items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-ink hover-underline">
            View All Reels <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>

      <div className="mt-12">
        <ShortsGrid />
      </div>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <Marquee />
    <AboutPreview />
    <Services />
    <ResidentialCommercial />
    <FounderSection />
    <ProjectsPreview />
    <WhyUs />
    <Process />
    <BrandsSection />
    <VideosSection />
    <FinalCTA />
  </>
);

export default Home;
